import { useMemo, useRef, useState } from "react";
import { AUDIENCE_LOOKALIKE_OPTIONS, getTemplateSpec } from "../utils";
import { useParams } from "react-router-dom";
import { Col, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import useCustomTableFilters from "hooks/useCustomTableFilters";
import { useAppSelector } from "redux/hooks";
import Table from "views/merchandise/tables/BulkActionsTable";
import { buildTextColumn } from "views/merchandise/columns/buildTextColumn";
import { toTitleCase } from "utils/formatText";
import { useFetch } from "hooks/api";
import { listFormatter } from "utils/listFormatter";
import { css } from "../../../../styled-system/css";
import Card from "components/core/basic/Card";
import {
  AUDIENCE_STATUS_FILTER,
  buildAudienceAsinsFilter,
  buildAudienceTagsFilter,
  buildAudienceTemplateFilter,
} from "./lifecycleFilters";
import { buildNumberColumn } from "views/merchandise/columns/buildNumberColumn";
import { formatCurrency, formatNumber } from "utils/formatNumber";
import CenteredModal from "components/core/basic/CenteredModal";
import AudienceForm from "./AudienceForm";
import moment from "moment";
import { FaClone } from "react-icons/fa6";
import { Button } from "components/core/basic/Button";
import { useAudienceTemplates } from "./useAudienceTemplates";
import { Flex, Grid } from "../../../../styled-system/jsx";
import { FaInfoCircle } from "react-icons/fa";
import { successToast } from "utils/toasts";
import { buildDateColumn } from "views/merchandise/columns/buildDateColumn";
import useQueryParams from "hooks/useQueryParams";
import { ButtonGroup } from "components/core/basic/ButtonGroup";
import ExpanderContainer from "components/core/basic/ExpanderContainer";
import { useQuery } from "@tanstack/react-query";
import api from "utils/api";
import PieChart from "components/core/blocks/PieChart";
import BarChart from "components/core/blocks/BarChart";
import { trellisPalette } from "components/custom/analytics/palettes";
import Loading from "components/core/blocks/Loading";
import Tabs from "components/core/basic/Tabs";

const useInsights = (insightType, audienceId, marketPlace, enabled) => {
  return useQuery({
    queryKey: [audienceId, marketPlace, insightType],
    queryFn: async () => {
      return await api.post(`/dsp/insights/insights/`, {
        audience: [{ negative: "false", groupId: "Group 1", audienceId: audienceId }],
        marketplace: marketPlace === "USA" ? "US" : marketPlace,
        insights_type: insightType,
      });
    },
    enabled,
    select: (res) => res.data,
  });
};

const Expander = ({ audience }) => {
  const [tab, setTab] = useState("categories");
  const audienceId = audience.dsp_canonical_id;
  const { marketPlace } = useAppSelector((state) => state.marketPlace);
  const status = audience.status;
  const isDSP = audience?.raw_amazon_body?.advertiserType === "DISPLAY";
  const { data: demographicsData, isLoading } = useInsights(
    "demographics",
    audienceId,
    marketPlace,
    isDSP && status === "SUCCEEDED",
  );
  const { data: categoriesData, isLoading: isLoadingCategories } = useInsights(
    "topCategoriesPurchased",
    audienceId,
    marketPlace,
    isDSP && status === "SUCCEEDED",
  );

  const defaultFormData = useMemo(() => {
    return {
      audience_name: audience.spec?.audience_name ?? "",
      template_name: audience.sql_template?.[0]?.template_name,
      asins: audience.sql_template?.[0]?.asins?.filter((asin) => asin),
      advertiser_id: audience.spec?.advertiser_id,
      description: audience.spec?.description ?? "",
      tags: audience.tags ?? [],
      refresh_rate_days: audience.spec?.refresh_rate_days,
      time_window_relative: true,
      time_window_start: new Date(moment(audience.spec?.time_window_start).format("YYYY-MM-DD")),
      time_window_end: new Date(moment().format("YYYY-MM-DD")),
      lookalike_audience_expected_reach: AUDIENCE_LOOKALIKE_OPTIONS?.find(
        (o) => o.value === audience.spec?.lookalike_audience_expected_reach,
      )?.value,
      views_range_lower: audience.spec?.views_range_lower ?? null,
      views_range_upper: audience.spec?.views_range_upper ?? null,
      search_term_fields: audience.sql_template?.[0]?.search_term_fields ?? [],
    };
  }, [audience]);

  if (!isDSP) {
    return (
      <div
        className={css({
          padding: "15px",
          lineHeight: "150px",
          fontWeight: "bold",
          fontSize: "1.5rem",
          color: "gray.500",
          background: "gray.100",
          borderRadius: "15px",
          textAlign: "center",
          height: "150px",
        })}
      >
        Demographic Overlap and Top Categories Purchased insights are only available for DSP audiences.
      </div>
    );
  }

  return (
    <>
      <Tabs
        tabs={[
          { label: "Top Categories Purchased", key: "categories" },
          { label: "Demographic Overlap", key: "demographics" },
          { label: "Configuration", key: "configuration" },
        ]}
        selectedTab={tab}
        onTabChange={setTab}
      />
      {tab === "categories" && (
        <>
          {isLoadingCategories && <Loading height={300} fullPage={false} />}
          {categoriesData?.retailCategories && (
            <BarChart
              title={"Categories"}
              metric="Overlap Percentage"
              colors={[trellisPalette[3]]}
              data={categoriesData.retailCategories}
              topN={20}
              hideOther
              accessor={(d) => d.insightMetrics.overlapPercentage}
              categoryAccessor={(d) => `${d.name}`}
              sortAccessor={(d) => d.insightMetrics.overlapPercentage}
              height={560}
            />
          )}
        </>
      )}
      {tab === "demographics" && (
        <>
          {isLoading && <Loading height={300} fullPage={false} />}
          {!demographicsData && !isLoading && (
            <div
              className={css({
                padding: "15px",
                lineHeight: "150px",
                fontWeight: "bold",
                fontSize: "1.5rem",
                color: "gray.500",
                background: "gray.100",
                borderRadius: "15px",
                textAlign: "center",
                height: "150px",
              })}
            >
              No Insights Found. Insights can take Amazon 1-2 weeks to generate.
            </div>
          )}
          <Grid gap={3} columns={2}>
            {demographicsData?.demographics?.gender && (
              <PieChart
                title={"Gender"}
                metric="Gender"
                data={demographicsData.demographics?.gender}
                nameAccessor={(d) => d.attribute}
                accessor={(d) => d.insightMetrics.overlapPercentage}
              />
            )}
            {demographicsData?.demographics?.age && (
              <BarChart
                title={"Age"}
                metric="Overlap Percentage"
                colors={[trellisPalette[3]]}
                data={demographicsData.demographics?.age}
                accessor={(d) => d.insightMetrics.overlapPercentage}
                categoryAccessor={(d) => `${d.range.min} - ${d.range.max}`}
                sortAccessor={(d) => d.range.min * 1000 + d.range.max}
                topN={8}
                height={340}
              />
            )}
            {demographicsData?.demographics?.income && (
              <BarChart
                title={"Income"}
                metric="Overlap Percentage"
                colors={[trellisPalette[3]]}
                data={demographicsData.demographics?.income}
                accessor={(d) => d.insightMetrics.overlapPercentage}
                categoryAccessor={(d) => {
                  if (!d.range.max) {
                    return `${formatCurrency(d.range.min, marketPlace, true, false, true)}+`;
                  }
                  return `${formatCurrency(d.range.min, marketPlace, true, false, true)} - ${formatCurrency(d.range.max, marketPlace, true, false, true)}`;
                }}
                sortAccessor={(d) => d.range.min}
                height={340}
              />
            )}
            {demographicsData?.demographics?.education && (
              <BarChart
                title={"Education"}
                metric="Overlap Percentage"
                colors={[trellisPalette[3]]}
                data={demographicsData.demographics?.education}
                accessor={(d) => d.insightMetrics.overlapPercentage}
                categoryAccessor={(d) => d.attribute}
                height={340}
              />
            )}
          </Grid>
        </>
      )}
      {tab === "configuration" && <AudienceForm defaultFormData={defaultFormData} readOnly />}
    </>
  );
};

const AMCAudienceDetails = () => {
  const [showAudienceForm, setShowAudienceForm] = useState(false);
  const [defaultFormData, setDefaultFormData] = useState({});
  const [audienceDetailsModalText, setAudienceDetailsModalText] = useState(null);

  const { id: audienceId } = useParams();
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const [status, setStatus] = useQueryParams("status", "active");

  const TABLE_ID = `amcAudienceDashboardDetails`;
  const tableRef = useRef();
  const filterProps = useCustomTableFilters(TABLE_ID);
  const { templates } = useAudienceTemplates();

  const { data, isLoading } = useFetch(
    [audienceId, marketPlace.marketPlace],
    "/amc/audiencespec/",
    { ordering: "-created_on" },
    {
      select: (res) => res.data.results,
      default: [],
    },
  );

  const tableData = useMemo(() => {
    return data?.map((audience) => {
      const { sql_template, raw_amazon_body } = audience;
      const { template_name } = sql_template[0] ?? {};
      const { audienceCount: audience_count } = raw_amazon_body ?? {};

      const templateSpec = getTemplateSpec(templates, template_name);
      const template_display_name = templateSpec?.audience_display_name;
      const recommended_tags = templateSpec?.recommended_tags;
      const advertiser_type = raw_amazon_body?.advertiserType
        ?.replace("DISPLAY", "DSP")
        ?.replace("SPONSORED_ADS", "Sponsored Ads");

      return { ...audience, template_display_name, audience_count, recommended_tags, advertiser_type };
    });
  }, [data, templates]);

  const filteredTableData = useMemo(() => {
    return tableData?.filter((audience) => {
      const { status: audienceStatus } = audience;
      return status === "active" ? audienceStatus !== "FAILED" : audienceStatus === "FAILED";
    });
  }, [tableData, status]);

  const audienceAsins = useMemo(() => {
    const asins = data?.flatMap((audience) => {
      const { sql_template } = audience;
      const { asins } = sql_template[0] ?? {};
      return asins;
    });
    return asins?.filter((a) => a);
  }, [data]);

  const ADDITIONAL_FILTERS = [
    buildAudienceTemplateFilter(templates),
    buildAudienceTagsFilter(templates),
    AUDIENCE_STATUS_FILTER,
    buildAudienceAsinsFilter(audienceAsins),
  ];

  const columnSpecs = useMemo(
    () => [
      buildTextColumn("status", "Status", {
        formatter: (v) => {
          return <span>{toTitleCase(v)}</span>;
        },
        hideFilter: true,
        width: 100,
      }),
      {
        key: "template_display_name",
        compareKey: null,
        options: {
          className: "text-start justify-content-start px-5 h-100",
          cellContainerClasses: "text-start justify-content-start",
          metric: {
            id: "template_display_name",
            format: "text",
            accessor: (d) => d.template_display_name,
            name: "Type",
          },
          width: 240,
          formatter: (v) => (
            <div className="text-start text-wrap">
              <span>{v}</span>
            </div>
          ),
          hideFilter: true,
        },
      },
      buildTextColumn("advertiser_type", "Advertiser Type", {
        formatter: (v) => {
          return <span>{v}</span>;
        },
        hideFilter: true,
        width: 100,
      }),
      {
        key: "asins",
        compareKey: null,
        options: {
          className: "text-start justify-content-start px-5 h-100",
          cellContainerClasses: "text-start justify-content-start",
          metric: {
            id: "asins",
            format: "text",
            accessor: (d) => d,
            name: "ASINS",
          },
          formatter: (v) => {
            const { asins } = v.sql_template?.[0] ?? {};

            return (
              (
                <div className="align-items-end d-flex gap-2">
                  <OverlayTrigger
                    overlay={
                      <Tooltip
                        placement="top"
                        className="in text-center"
                        id="tooltip-top"
                        bsPrefix="tr-popover-alt bg-purple"
                        style={{ maxWidth: "50%" }}
                      >
                        <span className="text-center mx-auto fs-5">
                          {listFormatter.format(asins?.filter((asin) => asin) ?? [])} <br />
                          <span
                            className={css({
                              textDecoration: "underline",
                              display: "block",
                            })}
                          >
                            Click to copy ASINs to clipboard
                          </span>
                        </span>
                      </Tooltip>
                    }
                  >
                    <div className="text-start text-wrap">
                      <span
                        className={css({ cursor: "pointer", overflow: "hidden", lineClamp: 4, WebkitLineClamp: 4 })}
                        onClick={() => {
                          navigator.clipboard.writeText(asins.join(","));
                          successToast({
                            message: "ASINs copied to clipboard",
                          });
                        }}
                      >
                        {listFormatter.format(asins?.filter((asin) => asin) ?? [])}
                      </span>
                    </div>
                  </OverlayTrigger>
                </div>
              ) ?? ""
            );
          },
          hideFilter: true,
        },
      },
      buildNumberColumn("audience_count", "Audience Count", {
        formatter: (v) => formatNumber(v),
      }),
      buildTextColumn("audience_execution_id", "Execution ID", {
        formatter: (v) => {
          return <span>{toTitleCase(v)}</span>;
        },
        hideFilter: true,
        width: 100,
        checked: false,
      }),
      buildTextColumn("dsp_audience_id", "Audience ID", { width: 150, checked: false }),
      buildTextColumn("dsp_canonical_id", "Canonical ID", {
        checked: false,
      }),
      {
        key: "details",
        compareKey: null,
        options: {
          checked: status === "failed",
          className: "text-start justify-content-start px-5 h-100",
          cellContainerClasses: "text-start justify-content-start",
          metric: {
            id: "details",
            format: "text",
            accessor: (d) => d,
            name: "Details",
          },
          formatter: (v) => {
            const { audience_execution_status_reason } = v;

            return (
              (
                <div className="d-flex justify-content-center text-muted">
                  {audience_execution_status_reason ?
                    <OverlayTrigger
                      overlay={
                        <Tooltip
                          placement="top"
                          className="in text-center"
                          id="tooltip-top"
                          bsPrefix="tr-popover-alt bg-purple"
                          style={{ maxWidth: "50%" }}
                        >
                          <div className="text-center mx-auto fs-5">{audience_execution_status_reason}</div>
                        </Tooltip>
                      }
                    >
                      <span
                        className={css({ cursor: "pointer", overflow: "hidden", lineClamp: 4, WebkitLineClamp: 4 })}
                      >
                        <FaInfoCircle onClick={() => setAudienceDetailsModalText(audience_execution_status_reason)} />
                      </span>
                    </OverlayTrigger>
                  : null}
                </div>
              ) ?? ""
            );
          },
          hideFilter: true,
        },
      },
      buildTextColumn("marketplace", "Marketplace", { checked: false }),
      buildDateColumn("created_on", "Created On"),
      {
        key: "actions",
        compareKey: null,
        options: {
          className: "text-start justify-content-start px-5 h-100",
          cellContainerClasses: "text-start justify-content-start",
          metric: {
            id: "actions",
            format: "text",
            accessor: (d) => d,
            name: "Actions",
            width: 30,
          },

          formatter: (v) => {
            return (
              <div className="d-flex justify-content-center text-muted">
                <OverlayTrigger
                  overlay={
                    <Tooltip
                      placement="top"
                      className="in text-center"
                      bsPrefix="tr-popover-alt bg-purple"
                      style={{ maxWidth: "50%" }}
                    >
                      <span className="text-center mx-auto fs-5">Recreate audience with updated parameters</span>
                    </Tooltip>
                  }
                >
                  <div>
                    <FaClone
                      onClick={() => {
                        const formData = {
                          audience_name: v.spec.audience_name ?? "",
                          template_name: v.sql_template?.[0]?.template_name,
                          asins: v.sql_template?.[0]?.asins?.filter((asin) => asin),
                          advertiser_id: v.spec.advertiser_id,
                          description: v.spec.description ?? "",
                          tags: v.tags ?? [],
                          refresh_rate_days: v.spec.refresh_rate_days,
                          time_window_relative: true,
                          time_window_start: new Date(moment(v.spec.time_window_start).format("YYYY-MM-DD")),
                          time_window_end: new Date(moment().format("YYYY-MM-DD")),
                          lookalike_audience_expected_reach: AUDIENCE_LOOKALIKE_OPTIONS?.find(
                            (o) => o.value === v.spec.lookalike_audience_expected_reach,
                          )?.value,
                          views_range_lower: v.spec.views_range_lower ?? null,
                          views_range_upper: v.spec.views_range_upper ?? null,
                          search_term_fields: v.sql_template?.[0]?.search_term_fields ?? [],
                        };

                        setDefaultFormData(formData);
                        setShowAudienceForm(true);
                      }}
                      className={css({ cursor: "pointer" })}
                    />
                  </div>
                </OverlayTrigger>
              </div>
            );
          },
        },
      },
    ],
    [status],
  );

  return (
    <Row className="pb-5">
      <Col xs={12}>
        <h3 className="pb-3 ps-5 fs-2" style={{ fontWeight: 600 }}>
          Audiences
        </h3>
        <CenteredModal
          show={showAudienceForm}
          closeModal={() => setShowAudienceForm(false)}
          dialogClassName="modal-60w"
          content={<AudienceForm setShowAudienceForm={setShowAudienceForm} defaultFormData={defaultFormData} />}
        />
        <CenteredModal
          show={audienceDetailsModalText}
          closeModal={() => setAudienceDetailsModalText(null)}
          dialogClassName="modal-60w"
          title={"Audience Failure Details"}
          content={
            <Flex flexDirection={"column"} gap={"large"} alignItems={"center"}>
              <p className="fs-4 text-center">{audienceDetailsModalText}</p>
              <Button onPress={() => setAudienceDetailsModalText(null)}>Continue</Button>
            </Flex>
          }
        />
        <Card>
          <div className="pb-5 w-100">
            <Flex justify={"space-between"} wrap={"wrap"} gap="small" alignContent={"center"} paddingBottom={"medium"}>
              <ButtonGroup
                buttons={[
                  {
                    label: "Active",
                    value: "active",
                  },
                  { label: "Failed", value: "failed" },
                ]}
                value={status}
                onSelect={setStatus}
              />
            </Flex>
            <Table
              marketPlace={marketPlace}
              tableRef={tableRef}
              tableId={TABLE_ID}
              additionalFilterOptions={ADDITIONAL_FILTERS}
              excludedFilterColumns={["title", "template_display_name", "details", "status", "asins"]}
              {...filterProps}
              data={filteredTableData}
              getSearchCriteria={(row) => {
                const {
                  spec: { audience_name },
                  template_display_name,
                } = row ?? {};

                return `${audience_name} ${template_display_name}`;
              }}
              hideBottomPagination={true}
              hideFilterToggleIcon={true}
              columnSpecs={columnSpecs}
              tableRowDescription={"Reports"}
              defaultSorted={[{ id: "id", desc: true }]}
              isLoading={isLoading}
              filterWidget={true}
              handleBulkChanges={() => {}}
              minRows={0}
              defaultPageSize={5}
              isSelectable={false}
              selectionKey="id"
              isExpandable={status === "active"}
              ExpandedContent={({ row }) => (
                <ExpanderContainer>
                  <Expander audience={row.original} />
                </ExpanderContainer>
              )}
              titleCol={{
                id: "title",
                isStatic: true,
                Header: "Audience",
                accessor: "text",
                format: "text",
                style: { width: "300px" },
                width: 300,
                Cell: (row) => {
                  const { audience_name: audienceName } = row.original?.spec ?? {};
                  return (
                    <span
                      style={{
                        fontWeight: 500,
                      }}
                    >
                      {audienceName}
                    </span>
                  );
                },
                Filter: () => null,
              }}
            />
            {!tableData?.length && !isLoading ?
              <div className="d-flex flex-column gap-3 align-items-center justify-content-center pt-5 pb-3">
                <p className="fs-4">
                  No audiences have been created yet. Create your first audience by clicking the button below.
                </p>
                <Button onPress={() => setShowAudienceForm(true)}>Create a New Audience</Button>
              </div>
            : null}
          </div>
        </Card>
      </Col>
    </Row>
  );
};

export default AMCAudienceDetails;
